import { useEffect, useRef } from 'react';
import { decodeToken } from 'react-jwt';

/**
 * Monitors a JWT token and triggers `onExpired` a few minutes before it expires.
 *
 * @param onExpired - Callback to execute when token is about to expire.
 * @param token - JWT access token string.
 * @param minutesBefore - Number of minutes before actual expiration to trigger the callback (default: 5)
 */
// Floor on the renewal delay so a token whose expiry falls inside the
// `minutesBefore` buffer (short-lived dev token, clock skew) can't retrigger
// `onExpired` in a near-zero-delay loop — each renewal always waits at least
// this long before the next one can fire.
const MIN_RENEWAL_DELAY_MS = 30_000;

export const useJwtExpired = (onExpired: () => void, token: string | null, minutesBefore: number = 5): void => {
  const expireHandleRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Clear any previous timeout when token changes
    if (expireHandleRef.current) {
      clearTimeout(expireHandleRef.current);
      expireHandleRef.current = null;
    }

    if (!token) return;

    const decoded = decodeToken<{ exp?: number; iat?: number }>(token);

    // If decoding fails or exp not present, exit
    if (!decoded || typeof decoded === 'string' || !decoded.exp) return;

    const expMs = decoded.exp * 1000; // JWT exp is in seconds
    const triggerTime = expMs - minutesBefore * 60 * 1000; // Trigger before expiry
    const delay = Math.max(triggerTime - Date.now(), MIN_RENEWAL_DELAY_MS);

    // Schedule expiration handler
    expireHandleRef.current = setTimeout(() => {
      expireHandleRef.current = null;
      onExpired();
    }, delay);

    // Cleanup on unmount or token change
    return () => {
      if (expireHandleRef.current) {
        clearTimeout(expireHandleRef.current);
        expireHandleRef.current = null;
      }
    };
  }, [token, onExpired, minutesBefore]);
};
